import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  Stack, Chip, Divider, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, FormControlLabel, Switch, Select,
} from '@mui/material';
import {
  Business as OrgIcon,
  Edit as EditIcon,
  Email as ResendIcon,
  Download as ExportIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Palette as BrandingIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { tenantAdminApi } from '../api/services';
import { useAuth } from '../auth/AuthProvider';


interface TenantInfo {
  id: string;
  name: string;
  domain?: string;
  status: string;
  plan: string;
  maxExecutiveSeats: number;
  maxEaSeats: number;
  currentExecutives: number;
  currentEAs: number;
  totalUsers: number;
  aiPlanningEnabled: boolean;
  transcriptProcessingEnabled: boolean;
  imageAnalysisEnabled: boolean;
  pdfExportEnabled: boolean;
  webhooksEnabled: boolean;
  apiAccessEnabled: boolean;
  monthlyAiTokenLimit: number;
  monthlyTranscriptLimit: number;
  trialExpiresAt?: string;
  subscriptionExpiresAt?: string;
}

interface TenantUser {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

const VALID_ROLES = ['Executive', 'ExecutiveAssistant', 'Admin', 'TenantAdmin'];

const ROLE_DESCRIPTIONS: Record<string, string> = {
  Executive: 'Plans and executes their day. Reviews AI-generated plans and approves schedules.',
  ExecutiveAssistant: 'Manages an executive\'s schedule. Creates work items, uploads images, and proposes daily plans.',
  Admin: 'Full tenant administration. Manages users, billing, branding, and organisation settings.',
  TenantAdmin: 'Full tenant administration. Manages users, billing, branding, and organisation settings.',
};

interface BrandingData {
  logoUrl: string;
  primaryColour: string;
  productName: string;
  tagline: string;
  supportEmail: string;
}

function SeatUsageBar({ label, current, max }: { label: string; current: number; max: number }) {
  const percent = max > 0 ? Math.min((current / max) * 100, 100) : 0;
  const isHigh = percent > 80;
  const isFull = percent >= 100;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: isFull ? 'error.main' : isHigh ? 'warning.main' : 'text.secondary' }}>
          {current} of {max} seats
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percent}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'rgba(255,255,255,0.06)',
          '& .MuiLinearProgress-bar': {
            bgcolor: isFull ? 'error.main' : isHigh ? 'warning.main' : 'primary.main',
            borderRadius: 4,
          },
        }}
      />
    </Box>
  );
}

function FeatureFlag({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {enabled
        ? <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
        : <CancelIcon sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.5 }} />}
      <Typography variant="body2" sx={{ color: enabled ? 'text.primary' : 'text.secondary' }}>
        {label}
      </Typography>
    </Stack>
  );
}

export default function TenantOrganisationPage() {
  const theme = useTheme();
  const { user } = useAuth();
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [usageTotals, setUsageTotals] = useState<{ aiTokensUsed: number; transcriptsProcessed: number } | null>(null);
  const [usageLimits, setUsageLimits] = useState<{ monthlyAiTokenLimit?: number; monthlyTranscriptLimit?: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Edit user dialog
  const [editUser, setEditUser] = useState<TenantUser | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editSaving, setEditSaving] = useState(false);

  // Branding
  const [branding, setBranding] = useState<BrandingData>({
    logoUrl: '', primaryColour: '#6C9BFF', productName: '', tagline: '', supportEmail: '',
  });
  const [brandingSaving, setBrandingSaving] = useState(false);

  // Financial Year
  const [useFinancialYear, setUseFinancialYear] = useState(false);
  const [fyStartMonth, setFyStartMonth] = useState(4);
  const [fyLabel, setFyLabel] = useState('');
  const [currentQuarter, setCurrentQuarter] = useState(0);
  const [currentWeek, setCurrentWeek] = useState(0);
  const [fySaving, setFySaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tenantResult, usersResult, brandingResult, usageResult, fyResult] = await Promise.allSettled([
        tenantAdminApi.getTenant(),
        tenantAdminApi.getUsers(),
        tenantAdminApi.getBranding(),
        tenantAdminApi.getUsage(),
        tenantAdminApi.getFinancialYear(),
      ]);

      if (tenantResult.status === 'fulfilled') setTenant(tenantResult.value);
      if (usersResult.status === 'fulfilled') setUsers(usersResult.value);
      if (brandingResult.status === 'fulfilled' && brandingResult.value) {
        setBranding(prev => ({ ...prev, ...brandingResult.value }));
      }
      if (usageResult.status === 'fulfilled') {
        const u = usageResult.value;
        setUsageTotals(u?.totals ?? null);
        setUsageLimits(u?.limits ?? null);
      }
      if (fyResult.status === 'fulfilled' && fyResult.value) {
        const fy = fyResult.value;
        setUseFinancialYear(fy.useFinancialYear);
        setFyStartMonth(fy.financialYearStartMonth);
        setFyLabel(fy.currentFYLabel);
        setCurrentQuarter(fy.currentQuarter);
        setCurrentWeek(fy.currentFYWeek);
      }

      if (tenantResult.status === 'rejected' && usersResult.status === 'rejected') {
        setError('Failed to load organisation data.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleEditOpen = (u: TenantUser) => {
    setEditUser(u);
    setEditRole(u.role);
    setEditActive(u.isActive);
  };

  const handleEditSave = async () => {
    if (!editUser) return;
    setEditSaving(true);
    setError(null);
    try {
      if (editRole !== editUser.role) {
        await tenantAdminApi.updateUserRole(editUser.id, editRole);
      }
      if (editActive !== editUser.isActive) {
        await tenantAdminApi.updateUserStatus(editUser.id, editActive);
      }
      setEditUser(null);
      setSuccess('User updated successfully.');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update user.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleResendWelcome = async (userId: string) => {
    setError(null);
    try {
      const result = await tenantAdminApi.resendWelcomeEmail(userId);
      setSuccess(result.message || 'Welcome email sent.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to send welcome email.');
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    setError(null);
    try {
      const response = await tenantAdminApi.exportData();
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const disposition = response.headers?.['content-disposition'];
      let filename = 'cadence-export.json';
      if (disposition) {
        const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match?.[1]) filename = match[1].replace(/['"]/g, '');
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSuccess('Data export downloaded successfully.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to export data.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleSaveBranding = async () => {
    setBrandingSaving(true);
    setError(null);
    try {
      await tenantAdminApi.updateBranding(branding);
      setSuccess('Branding updated successfully.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update branding.');
    } finally {
      setBrandingSaving(false);
    }
  };

  const handleSaveFY = async () => {
    setFySaving(true);
    setError(null);
    try {
      await tenantAdminApi.updateFinancialYear({
        useFinancialYear,
        startMonth: useFinancialYear ? fyStartMonth : undefined,
      });
      setSuccess('Financial year settings updated.');
      // Reload to get updated labels
      const fy = await tenantAdminApi.getFinancialYear();
      setFyLabel(fy.currentFYLabel);
      setCurrentQuarter(fy.currentQuarter);
      setCurrentWeek(fy.currentFYWeek);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update financial year settings.');
    } finally {
      setFySaving(false);
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Trialing': case 'Trial': return 'info';
      case 'Suspended': return 'warning';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const roleColor = (role: string) => {
    switch (role) {
      case 'Admin': case 'TenantAdmin': return 'error';
      case 'Executive': return 'primary';
      case 'ExecutiveAssistant': return 'secondary';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <OrgIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h4">My Organisation</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage your organisation, users, and view plan details
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Grid container spacing={2.5}>
        {/* Organisation Info Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
                Organisation Info
              </Typography>
              {tenant ? (
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Name
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                      {tenant.name}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={2}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Plan
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        {tenant.plan}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.25 }}>
                        <Chip label={tenant.status} size="small" color={statusColor(tenant.status) as any} sx={{ fontWeight: 600 }} />
                      </Box>
                    </Box>
                  </Stack>
                  {tenant.subscriptionExpiresAt && (
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Subscription Expires
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {new Date(tenant.subscriptionExpiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </Typography>
                    </Box>
                  )}
                  {tenant.domain && (
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Domain
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {tenant.domain}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              ) : !loading ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
                  Unable to load organisation details.
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        {/* Seat Usage Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
                Seat Usage
              </Typography>
              {tenant ? (
                <Stack spacing={3}>
                  <SeatUsageBar
                    label="Executive Seats"
                    current={tenant.currentExecutives}
                    max={tenant.maxExecutiveSeats}
                  />
                  <SeatUsageBar
                    label="EA Seats"
                    current={tenant.currentEAs}
                    max={tenant.maxEaSeats}
                  />
                  <Divider />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Total active users: {tenant.totalUsers}
                  </Typography>
                </Stack>
              ) : !loading ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
                  Unable to load seat usage.
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        {/* Feature Flags Card */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
                Plan Features
              </Typography>
              {tenant ? (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Stack spacing={1}>
                      <FeatureFlag label="AI Planning" enabled={tenant.aiPlanningEnabled} />
                      <FeatureFlag label="Transcript Processing" enabled={tenant.transcriptProcessingEnabled} />
                      <FeatureFlag label="Image Analysis" enabled={tenant.imageAnalysisEnabled} />
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Stack spacing={1}>
                      <FeatureFlag label="PDF Export" enabled={tenant.pdfExportEnabled} />
                      <FeatureFlag label="Webhooks" enabled={tenant.webhooksEnabled} />
                      <FeatureFlag label="API Access" enabled={tenant.apiAccessEnabled} />
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Stack spacing={1}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Monthly AI Token Limit: <strong style={{ color: theme.palette.text.primary }}>{tenant.monthlyAiTokenLimit.toLocaleString()}</strong>
                        {usageTotals && usageLimits?.monthlyAiTokenLimit && usageLimits.monthlyAiTokenLimit > 0 && (
                          <span style={{ color: (usageTotals.aiTokensUsed / usageLimits.monthlyAiTokenLimit) > 0.95 ? theme.palette.error.main : (usageTotals.aiTokensUsed / usageLimits.monthlyAiTokenLimit) > 0.8 ? theme.palette.warning.main : theme.palette.text.secondary }}>
                            {' '}({Math.round((usageTotals.aiTokensUsed / usageLimits.monthlyAiTokenLimit) * 100)}% used)
                          </span>
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Monthly Transcript Limit: <strong style={{ color: theme.palette.text.primary }}>{tenant.monthlyTranscriptLimit}</strong>
                        {usageTotals && usageLimits?.monthlyTranscriptLimit && usageLimits.monthlyTranscriptLimit > 0 && (
                          <span style={{ color: (usageTotals.transcriptsProcessed / usageLimits.monthlyTranscriptLimit) > 0.95 ? theme.palette.error.main : (usageTotals.transcriptsProcessed / usageLimits.monthlyTranscriptLimit) > 0.8 ? theme.palette.warning.main : theme.palette.text.secondary }}>
                            {' '}({Math.round((usageTotals.transcriptsProcessed / usageLimits.monthlyTranscriptLimit) * 100)}% used)
                          </span>
                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        ({tenant.maxExecutiveSeats} executive{tenant.maxExecutiveSeats !== 1 ? 's' : ''} x 1.5/day x 30 days)
                      </Typography>
                      {usageTotals && usageLimits && (
                        <>
                          {usageLimits.monthlyAiTokenLimit && usageLimits.monthlyAiTokenLimit > 0 && (usageTotals.aiTokensUsed / usageLimits.monthlyAiTokenLimit) > 0.8 && (
                            <Alert severity={(usageTotals.aiTokensUsed / usageLimits.monthlyAiTokenLimit) >= 0.95 ? 'error' : 'warning'} icon={<WarningAmberIcon fontSize="small" />} sx={{ py: 0.25, px: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                              AI tokens at {Math.round((usageTotals.aiTokensUsed / usageLimits.monthlyAiTokenLimit) * 100)}% of quota
                            </Alert>
                          )}
                          {usageLimits.monthlyTranscriptLimit && usageLimits.monthlyTranscriptLimit > 0 && (usageTotals.transcriptsProcessed / usageLimits.monthlyTranscriptLimit) > 0.8 && (
                            <Alert severity={(usageTotals.transcriptsProcessed / usageLimits.monthlyTranscriptLimit) >= 0.95 ? 'error' : 'warning'} icon={<WarningAmberIcon fontSize="small" />} sx={{ py: 0.25, px: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                              Transcripts at {Math.round((usageTotals.transcriptsProcessed / usageLimits.monthlyTranscriptLimit) * 100)}% of quota
                            </Alert>
                          )}
                        </>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              ) : !loading ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>
                  Unable to load feature information.
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        {/* Users Table */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Users
                </Typography>
              </Stack>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Login</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {u.displayName}
                            {u.id === user?.id && <Chip label="You" size="small" sx={{ ml: 1, height: 20 }} />}
                          </Typography>
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Chip label={u.role} size="small" color={roleColor(u.role) as any} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={u.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            color={u.isActive ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('en-GB') : 'Never'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(u.createdAt).toLocaleDateString('en-GB')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {u.id !== user?.id && (
                              <Tooltip title="Edit user">
                                <IconButton size="small" onClick={() => handleEditOpen(u)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Resend welcome email">
                              <IconButton size="small" onClick={() => handleResendWelcome(u.id)}>
                                <ResendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!loading && users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary" sx={{ py: 3 }}>No users found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Financial Year Settings Card */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Financial Year</Typography>

              <FormControlLabel
                control={<Switch checked={useFinancialYear} onChange={e => setUseFinancialYear(e.target.checked)} />}
                label="Use Financial Year (instead of Calendar Year)"
              />

              {useFinancialYear && (
                <Select
                  value={fyStartMonth}
                  onChange={e => setFyStartMonth(Number(e.target.value))}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  <MenuItem value={1}>January</MenuItem>
                  <MenuItem value={2}>February</MenuItem>
                  <MenuItem value={3}>March</MenuItem>
                  <MenuItem value={4}>April (UK Standard)</MenuItem>
                  <MenuItem value={5}>May</MenuItem>
                  <MenuItem value={6}>June</MenuItem>
                  <MenuItem value={7}>July (Australia)</MenuItem>
                  <MenuItem value={8}>August</MenuItem>
                  <MenuItem value={9}>September</MenuItem>
                  <MenuItem value={10}>October (US Federal)</MenuItem>
                  <MenuItem value={11}>November</MenuItem>
                  <MenuItem value={12}>December</MenuItem>
                </Select>
              )}

              <Typography variant="body2" color="text.secondary" mt={2}>
                Current: {fyLabel || 'Loading...'} &middot; Q{currentQuarter} &middot; Week {currentWeek}
              </Typography>

              <Button variant="contained" onClick={handleSaveFY} disabled={fySaving} sx={{ mt: 2 }}>
                {fySaving ? 'Saving...' : 'Save'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Branding Card */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <BrandingIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Branding
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
                Customise the look and feel of Cadence for your organisation.
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Logo URL"
                    fullWidth
                    value={branding.logoUrl}
                    onChange={e => setBranding(prev => ({ ...prev, logoUrl: e.target.value }))}
                    placeholder="https://example.com/logo.png"
                    helperText="URL to your organisation's logo"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Primary Colour"
                    fullWidth
                    value={branding.primaryColour}
                    onChange={e => setBranding(prev => ({ ...prev, primaryColour: e.target.value }))}
                    type="color"
                    InputLabelProps={{ shrink: true }}
                    helperText="Brand accent colour used throughout the interface"
                    inputProps={{ style: { height: 40 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Custom Product Name"
                    fullWidth
                    value={branding.productName}
                    onChange={e => setBranding(prev => ({ ...prev, productName: e.target.value }))}
                    placeholder="e.g. My Company Command Centre"
                    helperText="Replaces 'Cadence' in the UI for your users"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Tagline"
                    fullWidth
                    value={branding.tagline}
                    onChange={e => setBranding(prev => ({ ...prev, tagline: e.target.value }))}
                    placeholder="e.g. Executive excellence, every day"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Support Email"
                    type="email"
                    fullWidth
                    value={branding.supportEmail}
                    onChange={e => setBranding(prev => ({ ...prev, supportEmail: e.target.value }))}
                    placeholder="support@yourcompany.com"
                    helperText="Shown as the support contact for your users"
                  />
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveBranding}
                  disabled={brandingSaving}
                >
                  {brandingSaving ? 'Saving...' : 'Save Branding'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Data Export Card */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    Data Export
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    Download all your organisation's data as JSON (GDPR compliance).
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon />}
                  onClick={handleExport}
                  disabled={exportLoading}
                >
                  {exportLoading ? 'Exporting...' : 'Export Data'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Cancel Account */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ bgcolor: 'background.paper', border: '1px solid rgba(255, 82, 82, 0.2)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <WarningAmberIcon sx={{ color: 'error.main', fontSize: 20 }} />
                    <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 600 }}>
                      Cancel Account
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Permanently cancel your organisation's subscription and deactivate all users. This action cannot be undone.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  href="/billing"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Go to Billing
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {editUser && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {editUser.displayName} ({editUser.email})
              </Typography>
              <TextField
                label="Role"
                select
                fullWidth
                value={editRole}
                onChange={e => setEditRole(e.target.value)}
                helperText={ROLE_DESCRIPTIONS[editRole] || ''}
              >
                {VALID_ROLES.map(r => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Status"
                select
                fullWidth
                value={editActive ? 'active' : 'inactive'}
                onChange={e => setEditActive(e.target.value === 'active')}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving}>
            {editSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
